import { useState, useEffect } from "react";
import { GetOnlinePolicyConfig } from "../../../../Service/SearchPolicy";

/**
 * Server switches the Online Policy form has to mirror. Fetched from the
 * server and shared — every component asking gets the same answer.
 *
 * `multiProductPolicy` is the backend's ENABLE_MULTI_PRODUCT_POLICY. Off (also
 * the value while loading, and if the request fails) means one bundled job on
 * the OD company: only Brisk or the OD company can sell PA Cover, and the
 * wallet is charged the way it always has been.
 *
 * The answer is kept for CONFIG_TTL_MS only, not for the life of the page. It
 * used to be cached forever, so a page that had loaded while the flag was off
 * kept saying "off" — after the flag was switched on and the backend restarted
 * — until the browser tab was fully reloaded.
 */
// `coverChoice`: OD only / TP only policies are on (POLICY_COVER_CHOICE_ENABLED and the job-parts flow).
// `briskPaCover`: Brisk is offered as a PA Cover Company (BRISK_PA_COVER_ENABLED; on unless the server says false).
// `showUserCredentialUpdate`: the portal login settings are shown in Profile / User view (SHOW_USER_CRETENTIAL_UBDATE; hidden unless the server says true).
const DEFAULT_CONFIG = { multiProductPolicy: false, coverChoice: false, briskPaCover: true, showUserCredentialUpdate: false };
const CONFIG_TTL_MS = 30 * 1000;

let cached = null;
let cachedAt = 0;
let inflight = null;

const isFresh = () => cached && Date.now() - cachedAt < CONFIG_TTL_MS;

const loadConfig = () => {
  if (isFresh()) return Promise.resolve(cached);
  if (!inflight) {
    inflight = GetOnlinePolicyConfig().then((data) => {
      // A failed request is not cached, so the next screen tries again.
      if (data) {
        cached = { ...DEFAULT_CONFIG, ...data };
        cachedAt = Date.now();
      }
      inflight = null;
      return cached || DEFAULT_CONFIG;
    });
  }
  return inflight;
};

export const useOnlinePolicyConfig = () => {
  const [config, setConfig] = useState(cached || DEFAULT_CONFIG);

  useEffect(() => {
    let alive = true;
    loadConfig().then((c) => {
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  return config;
};
